"use client";

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Appointment, Customer, Vehicle, Staff } from "@/types";
import { PaymentMode } from "@/types/enums.types";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { format } from "date-fns";
import { userSubscriptionService } from "@/services/userSubscriptionService";
import {
  UserSubscriptionModelOutput,
  UserSubscriptionFeature,
} from "@/types/userSubscription.types";
import { Checkbox } from "@/components/ui/checkbox";
import { generateTimeSlots, formatCurrency } from "@/lib/utils";
import { toUTCString } from "@/lib/timezone";
import { VehicleMultiSelect } from "@/components/appointment/VehicleMultiSelect";
import { DiscountConfirmationDialog } from "@/components/appointment/DiscountConfirmationDialog";
import { AlertCircle, Crown } from "lucide-react";
import { paymentService } from "@/services/paymentService";
import { Badge } from "@/components/ui/badge";
import {
  APPOINTMENT_STATUS_OPTIONS,
  isAppointmentStatusLocked,
} from "@/lib/appointmentStatus";
import { useAuth } from "@/context/AuthContext";

// Generate time slots with 15-minute intervals
const timeSlots = generateTimeSlots();

interface AppointmentEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointment: Appointment | null;
  customers: Customer[];
  vehicles: Vehicle[];
  staff: Staff[];
}

export function AppointmentEditDialog({
  open,
  onOpenChange,
  appointment,
  customers,
  vehicles,
  staff,
}: AppointmentEditDialogProps) {
  const { updateAppointment, getSubscriptionById } = useApp();
  const { user } = useAuth();
  // Staff may only change the status of today's and future appointments.
  // The original date is used, so moving the date forward does not unlock it.
  const isStatusLocked = appointment
    ? isAppointmentStatusLocked(appointment.date, user?.role === "ADMIN")
    : false;
  const [loading, setLoading] = useState(false);
  const [userSubscription, setUserSubscription] =
    useState<UserSubscriptionModelOutput | null>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [subscriptionFeatures, setSubscriptionFeatures] = useState<
    UserSubscriptionFeature[]
  >([]);
  const [loadingFeatures, setLoadingFeatures] = useState(false);
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    date: new Date(),
    time: "09:00",
    staffId: "",
    paymentMode: "CASH" as PaymentMode,
    status: "PENDING" as Appointment["status"],
    address: "",
    remarks: "",
    bookingRef: "",
    bookingType: "" as "ONLINE" | "OFFLINE" | "SUBSCRIPTION" | "",
  });

  // Vehicle editing state
  const [selectedVehicleIds, setSelectedVehicleIds] = useState<string[]>([]);
  const [vehicleSearchTerm, setVehicleSearchTerm] = useState("");

  // Discount confirmation state
  const [showDiscountConfirm, setShowDiscountConfirm] = useState(false);
  const [pendingCustomDiscount, setPendingCustomDiscount] = useState<
    number | undefined
  >(undefined);
  const [existingPlatinumDiscount, setExistingPlatinumDiscount] = useState(0);

  // Fetch payment data to get platinum discount when dialog opens
  useEffect(() => {
    if (appointment?.id && open) {
      paymentService
        .getPaymentByRefId(appointment.id)
        .then((payment) => {
          if (payment) {
            setExistingPlatinumDiscount(payment.platinumDiscount || 0);
          } else {
            setExistingPlatinumDiscount(0);
          }
        })
        .catch((error) => {
          console.error("Error fetching payment for platinum discount:", error);
          setExistingPlatinumDiscount(0);
        });
    } else {
      setExistingPlatinumDiscount(0);
    }
  }, [appointment?.id, open]);

  // Initialize form data when appointment changes
  useEffect(() => {
    if (appointment) {
      const appointmentDate =
        appointment.date instanceof Date
          ? appointment.date
          : new Date(appointment.date);
      // Extract time from appointment date (format: HH:mm)
      const timeStr = format(appointmentDate, "HH:mm");
      setFormData({
        date: appointmentDate,
        time: timeStr,
        staffId: appointment.staffId || "",
        paymentMode: appointment.paymentMode,
        status: appointment.status,
        address: appointment.address || "",
        remarks: appointment.remarks || "",
        bookingRef: appointment.bookingRef || "",
        bookingType: (appointment.bookingType as "ONLINE" | "OFFLINE" | "SUBSCRIPTION" | "") || "",
      });
      // Reset selected features when appointment changes
      setSelectedFeatureIds([]);
      // Initialize selected vehicles
      setSelectedVehicleIds(appointment.vehicleIds || []);
      setVehicleSearchTerm("");
    }
  }, [appointment]);

  // Fetch user subscription data when appointment is USER_SUBSCRIPTION type
  useEffect(() => {
    if (appointment?.type === "USER_SUBSCRIPTION" && appointment.typeId) {
      setLoadingSubscription(true);
      setUserSubscription(null);
      userSubscriptionService
        .getUserSubscriptionById(appointment.typeId)
        .then((data) => {
          setUserSubscription(data);
        })
        .catch((error) => {
          console.error("Error fetching user subscription:", error);
          toast.error("Failed to load subscription details");
        })
        .finally(() => {
          setLoadingSubscription(false);
        });
    } else {
      setUserSubscription(null);
    }
  }, [appointment?.id, appointment?.type, appointment?.typeId]);

  // Fetch subscription features from new endpoint
  useEffect(() => {
    if (
      appointment?.type === "USER_SUBSCRIPTION" &&
      appointment.typeId &&
      open
    ) {
      setLoadingFeatures(true);
      setSubscriptionFeatures([]);
      userSubscriptionService
        .getUserSubscriptionFeatures(appointment.typeId)
        .then((features) => {
          setSubscriptionFeatures(features);
        })
        .catch((error) => {
          console.error("Error fetching subscription features:", error);
          toast.error("Failed to load subscription features");
        })
        .finally(() => {
          setLoadingFeatures(false);
        });
    } else {
      setSubscriptionFeatures([]);
    }
  }, [appointment?.id, appointment?.type, appointment?.typeId, open]);

  if (!appointment) return null;

  const customer = customers.find((c) => c.id === appointment.customerId);
  const customerVehicles = vehicles.filter(
    (v) => v.customerId === appointment.customerId,
  );
  const isUserSubscription = appointment.type === "USER_SUBSCRIPTION";

  // Discount calculation for confirmation flow
  const existingDiscount = appointment.discount || 0;
  const currentTotal = appointment.amount || 0;
  const originalVehicleIds = appointment.vehicleIds || [];

  // Calculate estimated price per vehicle and new total
  const estimatedPricePerVehicle =
    currentTotal && originalVehicleIds.length > 0
      ? currentTotal / originalVehicleIds.length
      : 0;
  const estimatedNewTotal =
    estimatedPricePerVehicle * selectedVehicleIds.length;

  // Check if vehicles have changed
  const vehiclesChanged =
    JSON.stringify([...selectedVehicleIds].sort()) !==
    JSON.stringify([...originalVehicleIds].sort());

  // Calculate new platinum discount if vehicles change (15% of new total for platinum members)
  // Note: platinumDiscount is auto-recalculated on the backend when total changes
  const estimatedNewPlatinumDiscount =
    existingPlatinumDiscount > 0 && vehiclesChanged
      ? estimatedNewTotal * 0.15
      : existingPlatinumDiscount;

  // Check if the price adjustment will be affected by vehicle changes.
  // Only a reduction (positive backend discount) gets capped by the backend;
  // a surcharge is stored as a negative value and is never capped, so it must
  // not be treated as "will be adjusted" here.
  const discountWillBeAdjusted =
    existingDiscount > 0 &&
    estimatedNewTotal < existingDiscount &&
    vehiclesChanged;

  // A surcharge still needs the user to see what happens to the total, even
  // though it survives the vehicle change untouched.
  const hasPriceAdjustment = existingDiscount !== 0;

  // Handle feature selection
  const handleFeatureToggle = (featureId: string) => {
    setSelectedFeatureIds((prev) =>
      prev.includes(featureId)
        ? prev.filter((id) => id !== featureId)
        : [...prev, featureId],
    );
  };

  // Handle save button click - check for discount confirmation
  const handleSaveClick = () => {
    // Validate remarks for certain statuses
    if (
      ["POSTPONED", "CANCELLED", "NOT_COMPLETED"].includes(formData.status) &&
      !formData.remarks.trim()
    ) {
      toast.error("Please provide remarks for this status");
      return;
    }

    // If vehicles changed and a price adjustment exists, confirm before saving.
    // Checks !== 0 so surcharges (stored negative) are caught too -- they were
    // previously skipped and silently repriced.
    if (vehiclesChanged && hasPriceAdjustment) {
      setShowDiscountConfirm(true);
    } else {
      // No discount confirmation needed, save directly
      performSave();
    }
  };

  // Perform the actual save
  const performSave = async (customDiscount?: number) => {
    setLoading(true);
    try {
      // Combine date and time into a single Date object
      const dateStr = format(formData.date, "yyyy-MM-dd");
      const combinedDateTime = new Date(`${dateStr}T${formData.time}`);

      const updateData: Partial<Appointment> & {
        featureIds?: string[];
        bookingRef?: string;
        vehicleIds?: string[];
        bookingType?: "ONLINE" | "OFFLINE" | "SUBSCRIPTION";
      } = {
        // Properly convert local time to UTC ISO string for backend storage
        date: toUTCString(combinedDateTime) as unknown as Date,
        paymentMode: formData.paymentMode,
        status: formData.status,
      };

      if (formData.bookingType) {
        updateData.bookingType = formData.bookingType as "ONLINE" | "OFFLINE" | "SUBSCRIPTION";
      }

      if (formData.staffId) {
        updateData.staffId = formData.staffId;
      }

      // Include vehicleIds if changed
      if (selectedVehicleIds.length > 0) {
        updateData.vehicleIds = selectedVehicleIds;
      }

      // Include bookingRef if it has a value
      if (formData.bookingRef) {
        updateData.bookingRef = formData.bookingRef;
      }

      // Only include address if it has a value
      if (formData.address) {
        updateData.address = formData.address;
      }

      // Only include remarks if it has a value
      if (formData.remarks) {
        updateData.remarks = formData.remarks;
      }

      // Include featureIds if features are selected
      if (selectedFeatureIds.length > 0) {
        updateData.featureIds = selectedFeatureIds;
      }

      // Step 1: Update appointment (includes vehicle changes)
      await updateAppointment(appointment.id, updateData);

      // Step 2: If custom discount provided, update via payment endpoint
      if (customDiscount !== undefined && vehiclesChanged) {
        try {
          const payment = await paymentService.getPaymentByRefId(
            appointment.id,
          );
          if (payment) {
            await paymentService.updateDiscount(payment.id, customDiscount);
            toast.success("Appointment updated", {
              description: `New discount: ${formatCurrency(customDiscount)}`,
            });
          } else {
            toast.success("Appointment updated", {
              description:
                "Note: Could not update discount - payment not found.",
            });
          }
        } catch (discountError) {
          console.error("Error updating discount:", discountError);
          toast.warning("Appointment updated, but discount update failed", {
            description: "Please update the discount manually in billing.",
          });
        }
      } else if (discountWillBeAdjusted && customDiscount === undefined) {
        toast.warning("Appointment updated", {
          description: `Discount will be adjusted by the system`,
        });
      } else {
        toast.success("Appointment updated successfully");
      }

      onOpenChange(false);
    } catch (error: any) {
      toast.error(error?.message || "Failed to update appointment");
    } finally {
      setLoading(false);
      setShowDiscountConfirm(false);
    }
  };

  // Discount confirmation handlers
  const handleConfirmWithAdjustment = () => {
    performSave(); // Auto-adjusted discount
  };

  const handleConfirmWithNewDiscount = (discount: number) => {
    performSave(discount); // Custom discount
  };

  const handleConfirmCancel = () => {
    setShowDiscountConfirm(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Appointment</DialogTitle>
            <DialogDescription>Update appointment details</DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Booking Reference */}
            <div className="grid gap-2">
              <Label>Booking Ref No.</Label>
              <Input
                value={formData.bookingRef}
                onChange={(e) =>
                  setFormData({ ...formData, bookingRef: e.target.value })
                }
                placeholder="e.g., WC-0001"
              />
            </div>

            {/* Booking Type */}
            <div className="grid gap-2">
              <Label>Booking Type</Label>
              <Select
                value={formData.bookingType}
                onValueChange={(value: "ONLINE" | "OFFLINE" | "SUBSCRIPTION") =>
                  setFormData({ ...formData, bookingType: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select booking type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ONLINE">Online</SelectItem>
                  <SelectItem value="OFFLINE">Offline</SelectItem>
                  <SelectItem value="SUBSCRIPTION">Subscription</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Customer & Vehicle (Read-only) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Customer Name</Label>
                <Input
                  value={customer?.name || "N/A"}
                  disabled
                  className="bg-muted"
                />
              </div>
              <div className="grid gap-2">
                <Label>Vehicle Details *</Label>
                <VehicleMultiSelect
                  vehicles={customerVehicles}
                  selectedVehicleIds={selectedVehicleIds}
                  onSelectionChange={setSelectedVehicleIds}
                  searchTerm={vehicleSearchTerm}
                  onSearchChange={setVehicleSearchTerm}
                  error={
                    selectedVehicleIds.length === 0
                      ? "At least one vehicle is required"
                      : undefined
                  }
                />
              </div>
            </div>

            {/* Platinum Member Discount Info */}
            {existingPlatinumDiscount > 0 && (
              <div className="p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex items-center gap-2 text-sm">
                  <Crown className="h-4 w-4 text-amber-600" />
                  <span className="font-medium text-amber-800 dark:text-amber-400">
                    Platinum Member Discount Applied
                  </span>
                  <Badge variant="secondary" className="ml-auto bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-300">
                    {formatCurrency(existingPlatinumDiscount)} (15%)
                  </Badge>
                </div>
                {vehiclesChanged && (
                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                    Note: Platinum discount will auto-recalculate to {formatCurrency(estimatedNewPlatinumDiscount)} when vehicles change.
                  </p>
                )}
              </div>
            )}

            {/* Date */}
            <div className="grid gap-2">
              <Label>Schedule Date *</Label>
              <Input
                type="date"
                value={format(formData.date, "yyyy-MM-dd")}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    date: new Date(e.target.value),
                  })
                }
              />
            </div>

            {/* Time */}
            <div className="grid gap-2">
              <Label>Schedule Time *</Label>
              <Select
                value={formData.time || "09:00"}
                onValueChange={(value) =>
                  setFormData({
                    ...formData,
                    time: value,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map((slot) => (
                    <SelectItem key={slot.value} value={slot.value}>
                      {slot.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Staff */}
            <div className="grid gap-2">
              <Label>Staff</Label>
              <Select
                value={formData.staffId}
                onValueChange={(value) =>
                  setFormData({ ...formData, staffId: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select staff" />
                </SelectTrigger>
                <SelectContent>
                  {staff.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground">
                      No staff available
                    </div>
                  ) : (
                    staff.map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Payment Mode */}
            <div className="grid gap-2">
              <Label>Payment Mode</Label>
              <Select
                value={formData.paymentMode}
                onValueChange={(value: PaymentMode) =>
                  setFormData({ ...formData, paymentMode: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CASH">Cash</SelectItem>
                  <SelectItem value="CREDIT_CARD">Credit Card</SelectItem>
                  <SelectItem value="DEBIT_CARD">Debit Card</SelectItem>
                  <SelectItem value="BANK_TRANSFER">Bank Transfer</SelectItem>
                  <SelectItem value="POS">POS</SelectItem>
                  <SelectItem value="APPLE_PAY">Apple Pay</SelectItem>
                  <SelectItem value="G_PAY">G-Pay</SelectItem>
                  <SelectItem value="WALLET">Wallet</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Appointment Status */}
            <div className="grid gap-2">
              <Label>Appointment Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value: Appointment["status"]) =>
                  setFormData({ ...formData, status: value })
                }
                disabled={isStatusLocked}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {APPOINTMENT_STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isStatusLocked && (
                <p className="text-xs text-muted-foreground">
                  This appointment has already passed. Its status is locked and
                  can only be changed by an administrator.
                </p>
              )}
            </div>

            {/* Remarks - Required for certain statuses */}
            {(formData.status === "POSTPONED" ||
              formData.status === "CANCELLED" ||
              formData.status === "NOT_COMPLETED") && (
              <div className="grid gap-2">
                <Label>Remarks *</Label>
                <Textarea
                  value={formData.remarks}
                  onChange={(e) =>
                    setFormData({ ...formData, remarks: e.target.value })
                  }
                  placeholder="Enter reason for status change"
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  Required when status is Postponed, Cancelled, or Not Completed
                </p>
              </div>
            )}

            {/* Subscription Features Selection (USER_SUBSCRIPTION) */}
            {isUserSubscription && (
              <div className="grid gap-2 border-t pt-4">
                <Label className="text-base font-semibold">
                  Subscription Features
                </Label>
                <p className="text-sm text-muted-foreground mb-3">
                  Select features to use for this appointment
                </p>

                {loadingFeatures ? (
                  <div className="p-4 text-center text-muted-foreground">
                    Loading features...
                  </div>
                ) : subscriptionFeatures.length === 0 ? (
                  <div className="p-4 text-center text-muted-foreground">
                    No features available
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {subscriptionFeatures.map((feature) => {
                      const isSelected = selectedFeatureIds.includes(
                        feature.id,
                      );
                      const isNumeric =
                        typeof feature.value === "number" ||
                        (typeof feature.value === "string" &&
                          feature.value !== "" &&
                          !isNaN(Number(feature.value)));
                      const isBoolean = feature.type === "Boolean";

                      return (
                        <div
                          key={feature.id}
                          className="flex items-center justify-between p-3 border rounded hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <Checkbox
                              id={`feature-${feature.id}`}
                              checked={isSelected}
                              onCheckedChange={() =>
                                handleFeatureToggle(feature.id)
                              }
                            />
                            <div className="flex-1">
                              <Label
                                htmlFor={`feature-${feature.id}`}
                                className="text-sm font-medium cursor-pointer"
                              >
                                {feature.name}
                              </Label>
                              <div className="text-xs text-muted-foreground mt-1">
                                {isBoolean ? (
                                  <span
                                    className={
                                      feature.value
                                        ? "text-green-600"
                                        : "text-gray-500"
                                    }
                                  >
                                    {feature.value ? "Enabled" : "Disabled"}
                                  </span>
                                ) : isNumeric ? (
                                  <span>Balance: {feature.value}</span>
                                ) : (
                                  <span>Type: {feature.type}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {selectedFeatureIds.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {selectedFeatureIds.length} feature(s) selected
                  </p>
                )}
              </div>
            )}

            {/* Address */}
            <div className="grid gap-2">
              <Label>Address</Label>
              <Textarea
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
                placeholder="Enter service address"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveClick} disabled={loading}>
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discount Confirmation Dialog */}
      <DiscountConfirmationDialog
        open={showDiscountConfirm}
        onOpenChange={setShowDiscountConfirm}
        currentDiscount={existingDiscount}
        currentTotal={currentTotal}
        newTotal={estimatedNewTotal}
        platinumDiscount={estimatedNewPlatinumDiscount}
        onConfirmWithAdjustment={handleConfirmWithAdjustment}
        onConfirmWithNewDiscount={handleConfirmWithNewDiscount}
        onCancel={handleConfirmCancel}
      />
    </>
  );
}
